import React from "react";
import { FiChevronUp, FiChevronDown } from "react-icons/fi";
import { AvatarRing } from "../../../common/comms/AvatarRing";
import { StatusBadge } from "../../../common/comms/StatusBadge";
import { Chip } from "../../../common/comms/Chip";
import { useCommsIdentity } from "../../../hooks/comms/useCommsIdentity";
import {
  formatDuration,
  formatSecondsBrief,
  pct,
  resolvedId,
} from "../../../utils/oversightFormat";

const SLA_SUCCESS_THRESHOLD = 0.9;
const SLA_NEUTRAL_THRESHOLD = 0.7;

/** Column definitions in display order. `key` is the sort/payload field; null = unsortable. */
const COLUMNS = [
  { key: null, label: "Coach" },
  { key: "online_seconds_today", label: "Online today" },
  { key: "online_seconds", label: "Online total" },
  { key: "sessions_count", label: "Sessions" },
  { key: "messages_sent", label: "Msgs sent" },
  // Two different room numbers, and they are NOT nested: `total_rooms` is
  // every room the coach is currently in (point in time, not range-scoped),
  // `active_rooms` is the rooms they actually messaged in during the range.
  { key: "total_rooms", label: "Rooms" },
  { key: "active_rooms", label: "Rooms messaged" },
  { key: "median_first_response_seconds", label: "Median first response" },
  { key: "sla_hit_rate", label: "SLA hit rate" },
  { key: "csat_avg", label: "CSAT" },
  { key: "crisis_count", label: "Crisis" },
  { key: "ai_interventions", label: "AI interventions" },
  { key: null, label: "" },
];

/**
 * Next sort state for a header click, cycling per column: none → desc → asc → none.
 * @param {{key: string|null, dir: 'asc'|'desc'|null}} currentSort
 * @param {string} key - Column field name being clicked.
 * @returns {{key: string|null, dir: 'asc'|'desc'|null}}
 */
function nextSort(currentSort, key) {
  if (currentSort.key !== key) return { key, dir: "desc" };
  if (currentSort.dir === "desc") return { key, dir: "asc" };
  return { key: null, dir: null };
}

/**
 * SLA hit rate StatusBadge tone: success ≥0.9, neutral 0.7–0.9, danger <0.7, neutral when null.
 * @param {number|null|undefined} rate
 * @returns {'success'|'neutral'|'danger'}
 */
function slaTone(rate) {
  if (rate == null || isNaN(rate)) return "neutral";
  if (rate >= SLA_SUCCESS_THRESHOLD) return "success";
  if (rate >= SLA_NEUTRAL_THRESHOLD) return "neutral";
  return "danger";
}

/** One sortable (or static) `<th>`. */
function TableHeaderCell({ column, sort, onSortChange }) {
  const baseClass =
    "sticky top-0 z-10 bg-white px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400 border-b border-gray-200 whitespace-nowrap";

  if (!column.key) {
    return (
      <th scope="col" className={baseClass}>
        {column.label}
      </th>
    );
  }

  const isActive = sort.key === column.key;

  return (
    <th
      scope="col"
      className={baseClass}
      aria-sort={isActive ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <button
        type="button"
        onClick={() => onSortChange(nextSort(sort, column.key))}
        className={`inline-flex items-center gap-1 rounded hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-primary/30 ${
          isActive ? "text-gray-600" : ""
        }`}
      >
        <span>{column.label}</span>
        {isActive &&
          (sort.dir === "asc" ? (
            <FiChevronUp size={12} aria-hidden="true" />
          ) : (
            <FiChevronDown size={12} aria-hidden="true" />
          ))}
      </button>
    </th>
  );
}

/** Crisis count cell: danger badge when >0, plain text otherwise (never NaN/undefined). */
function CrisisCell({ value }) {
  if (value == null || isNaN(value)) {
    return <span className="text-gray-400">—</span>;
  }
  if (value > 0) {
    return <StatusBadge tone="danger">{value}</StatusBadge>;
  }
  return <span className="text-gray-700">{value}</span>;
}

/** One coach analytics row. */
function CoachRow({ coach, index, onOpenCoach, onViewRooms, getCoachName }) {
  const id = resolvedId(coach);
  const colorIndex = index % 8;
  const name =
    coach?.coach_name || getCoachName(id) || (id ? `Coach ${String(id).slice(-4)}` : "Coach");
  const presence = coach?.is_online_now ? "online" : "offline";

  // Only the "live" chip is conditional, not the value: `formatDuration` prints
  // a measured zero as "0m" and an absent one as "—", and "— live" would assert
  // liveness about a number that isn't there.
  const onlineToday = coach?.online_seconds_today;
  const hasOnlineToday = onlineToday != null && !isNaN(onlineToday);

  const medianReply = coach?.median_first_response_seconds;
  const avgReply = coach?.avg_first_response_seconds;
  const p90Reply = coach?.p90_first_response_seconds;
  const replyTitle = `avg ${formatSecondsBrief(avgReply)} · p90 ${formatSecondsBrief(p90Reply)}`;

  const peakRooms = coach?.peak_daily_active_rooms;
  const roomsMessagedTitle =
    peakRooms == null
      ? "Distinct rooms messaged in this range"
      : `Distinct rooms messaged in this range · busiest single day ${peakRooms}`;

  const csatAvg = coach?.csat_avg;
  const csatCount = coach?.csat_count ?? 0;

  const handleRowClick = () => onOpenCoach(coach);
  const handleNameClick = (e) => {
    e.stopPropagation();
    onOpenCoach(coach);
  };
  const handleViewRoomsClick = (e) => {
    e.stopPropagation();
    onViewRooms(coach);
  };

  return (
    <tr
      className="border-b border-gray-100 text-sm text-gray-700 hover:bg-orange-50/40 cursor-pointer"
      onClick={handleRowClick}
    >
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={handleNameClick}
          className="flex items-center gap-3 text-left focus:outline-none focus:ring-2 focus:ring-orange-primary/30 rounded"
        >
          <AvatarRing name={name} colorIndex={colorIndex} size="md" presence={presence} />
          <span className="font-semibold text-gray-800 truncate max-w-[180px]">{name}</span>
        </button>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <span className="inline-flex items-center gap-2">
          {formatDuration(onlineToday)}
          {hasOnlineToday && <Chip tone="info">live</Chip>}
        </span>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">{formatDuration(coach?.online_seconds)}</td>
      <td className="px-4 py-3">{coach?.sessions_count ?? "—"}</td>
      <td className="px-4 py-3">{coach?.messages_sent ?? "—"}</td>
      <td className="px-4 py-3" title="Rooms this coach is in right now">
        {coach?.total_rooms ?? "—"}
      </td>
      <td className="px-4 py-3" title={roomsMessagedTitle}>
        {coach?.active_rooms ?? "—"}
      </td>
      <td className="px-4 py-3 whitespace-nowrap" title={replyTitle}>
        {formatSecondsBrief(medianReply)}
      </td>
      <td className="px-4 py-3">
        <StatusBadge tone={slaTone(coach?.sla_hit_rate)}>{pct(coach?.sla_hit_rate)}</StatusBadge>
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        {csatAvg == null || isNaN(csatAvg) ? (
          <span className="text-gray-400">—</span>
        ) : (
          <span>
            {Number(csatAvg).toFixed(1)}{" "}
            <span className="text-gray-400">({csatCount})</span>
          </span>
        )}
      </td>
      <td className="px-4 py-3">
        <CrisisCell value={coach?.crisis_count} />
      </td>
      <td className="px-4 py-3">{coach?.ai_interventions ?? "—"}</td>
      <td className="px-4 py-3 text-right">
        <button
          type="button"
          onClick={handleViewRoomsClick}
          className="min-h-[44px] inline-flex items-center px-2 text-xs font-medium text-orange-primary hover:underline focus:outline-none focus:ring-2 focus:ring-orange-primary/30 rounded"
        >
          View rooms
        </button>
      </td>
    </tr>
  );
}

/**
 * Desktop/tablet coach analytics table (`sm:` and up).
 *
 * Real columns (payload field names from `GET /oversight/analytics/coaches`), client-side
 * sortable headers (`sort`/`onSortChange` driven by the parent, which filters/sorts `coaches`
 * before handing them down), and row-level navigation into the coach detail page or the
 * rooms tab.
 *
 * @param {Object} props
 * @param {Array<Object>} props.coaches - Filtered + sorted coach analytics rows.
 * @param {boolean} props.loading - True while the coaches page is (re)loading.
 * @param {{key: string|null, dir: 'asc'|'desc'|null}} props.sort - Current sort state.
 * @param {(next: {key: string|null, dir: 'asc'|'desc'|null}) => void} props.onSortChange - Sort setter.
 * @param {(coach: Object) => void} props.onOpenCoach - Opens a coach's detail page.
 * @param {(coach: Object) => void} props.onViewRooms - Jumps to the Rooms tab filtered to this coach.
 */
export function CoachSummaryTable({
  coaches,
  loading,
  sort,
  onSortChange,
  onOpenCoach,
  onViewRooms,
}) {
  const { getCoachName } = useCommsIdentity();

  if (loading) {
    return null;
  }

  const rows = coaches || [];

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1200px] text-left">
          <thead>
            <tr>
              {COLUMNS.map((column, index) => (
                <TableHeaderCell
                  key={column.key || `col-${index}`}
                  column={column}
                  sort={sort}
                  onSortChange={onSortChange}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((coach, index) => (
              <CoachRow
                key={resolvedId(coach) ?? index}
                coach={coach}
                index={index}
                onOpenCoach={onOpenCoach}
                onViewRooms={onViewRooms}
                getCoachName={getCoachName}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CoachSummaryTable;
